import { router } from "@inertiajs/react";

// Transparent recovery for stale CSRF tokens.
//
// Why: long-lived tabs (overnight idle, browser session-restore, deploys
// that rotate secret_key_base) end up with a `<meta name="csrf-token">`
// value that no longer matches the server's session. The user clicks
// "submit" and the request 419s — which used to feel like the app was
// broken.
//
// What: capture every unsafe Inertia visit's URL/method/data/options in
// `router.on('before')`. When the response comes back as 419 *with the
// server's `X-CSRF-Expired: true` marker*, hit the `/csrf` endpoint to
// land a fresh `XSRF-TOKEN` cookie, then re-issue the original visit
// via `router.visit(url, opts)` tagged with `X-Inertia-Csrf-Replay: 1`
// so the `before` hook can tell our internal replay apart from a user
// click.
//
// Double-submit guard: while a recovery is in flight, `useForm.processing`
// flips back to false on the failed visit, so the submit button briefly
// re-enables. To stop a frustrated double-click from producing two
// logs, we cancel any user-initiated unsafe visit during the recovery
// window (the replay we issue ourselves carries the replay header and
// is allowed through).

const CSRF_STATUS = 419;
const CSRF_REFRESH_PATH = "/csrf";
const REPLAY_HEADER = "X-Inertia-Csrf-Replay";
const CSRF_EXPIRED_HEADER = "x-csrf-expired";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

// Per-visit callbacks Inertia attaches to the runtime visit object. We
// forward them onto the replay so `useForm` (which wraps these to
// drive `processing`/`errors`/`recentlySuccessful`) and any caller
// `onSuccess: () => setOpen(false)` / `onFinish: () => setSubmitting(false)`
// still fire when the original 419 recovers.
const CALLBACK_NAMES = [
  "onCancelToken",
  "onBefore",
  "onStart",
  "onProgress",
  "onFinish",
  "onCancel",
  "onSuccess",
  "onError",
];

let pendingVisit = null;
let recoveryInProgress = false;

export function installCsrfRecovery() {
  if (typeof window === "undefined") return;
  if (window.__pagePledgeCsrfRecoveryInstalled) return;
  window.__pagePledgeCsrfRecoveryInstalled = true;

  syncCsrfMetaFromCookie();

  router.on("before", (event) => {
    const visit = event.detail?.visit;
    if (!visit) return;

    const method = String(visit.method || "get").toLowerCase();
    if (!UNSAFE_METHODS.has(method)) return;

    const isInternalReplay = readHeader(visit.headers, REPLAY_HEADER) === "1";

    // Block duplicate user submits while a recovery is mid-flight. The
    // replay we issue ourselves carries the replay header and passes.
    if (recoveryInProgress && !isInternalReplay) {
      event.preventDefault?.();
      return false;
    }

    if (!isInternalReplay) pendingVisit = snapshotVisit(visit, method);
  });

  router.on("invalid", (event) => {
    if (!isCsrfFailure(event.detail?.response)) return;
    event.preventDefault();
    replayWithFreshToken();
  });

  router.on("exception", (event) => {
    if (!isCsrfFailure(event.detail?.exception?.response)) return;
    event.preventDefault();
    replayWithFreshToken();
  });

  router.on("success", () => {
    // Drop any retained snapshot now that the visit it represented has
    // succeeded — the data may include FormData/file blobs (e.g. CSV
    // imports) we don't want to hold past the visit's lifetime.
    pendingVisit = null;
    syncCsrfMetaFromCookie();
  });
  window.addEventListener("pageshow", syncCsrfMetaFromCookie);
}

function isCsrfFailure(response) {
  if (!response) return false;
  if (response.status !== CSRF_STATUS) return false;
  // Only replay 419s that the server explicitly marked as ours. Any
  // other 419 (proxy, middleware, future code path) falls through to
  // Inertia's default error handling.
  return readHeader(response.headers, CSRF_EXPIRED_HEADER) === "true";
}

function snapshotVisit(visit, method) {
  const callbacks = {};
  for (const name of CALLBACK_NAMES) {
    if (typeof visit[name] === "function") callbacks[name] = visit[name];
  }

  return {
    url: visit.url instanceof URL ? visit.url.href : String(visit.url ?? ""),
    method,
    data: visit.data,
    headers: visit.headers ? { ...visit.headers } : undefined,
    forceFormData: visit.forceFormData === true,
    preserveScroll: visit.preserveScroll,
    preserveState: visit.preserveState,
    errorBag: visit.errorBag ?? null,
    queryStringArrayFormat: visit.queryStringArrayFormat,
    only: Array.isArray(visit.only) ? [...visit.only] : undefined,
    except: Array.isArray(visit.except) ? [...visit.except] : undefined,
    replace: visit.replace === true,
    fresh: visit.fresh === true,
    reset: Array.isArray(visit.reset) ? [...visit.reset] : undefined,
    preserveUrl: visit.preserveUrl === true,
    showProgress: visit.showProgress,
    async: visit.async === true,
    callbacks,
  };
}

async function replayWithFreshToken() {
  // A second 419 fired while a recovery was already in flight means
  // /csrf didn't actually refresh the token, or the session is fully
  // gone. Hard-reload so the user gets a fresh page (and sign-in if
  // their session is truly destroyed) rather than a silently dropped
  // submit.
  if (recoveryInProgress) {
    window.location.assign(window.location.href);
    return;
  }
  // No captured visit to replay (419 fired before any unsafe submit
  // we could intercept). Hard-reload for the same reason — silent
  // drops are worse than a fresh page.
  if (!pendingVisit) {
    window.location.assign(window.location.href);
    return;
  }

  recoveryInProgress = true;
  const visit = pendingVisit;
  pendingVisit = null;

  try {
    await refreshCsrfToken();
    syncCsrfMetaFromCookie();

    const userOnFinish = visit.callbacks.onFinish;

    router.visit(visit.url, {
      method: visit.method,
      data: visit.data,
      headers: { ...(visit.headers || {}), [REPLAY_HEADER]: "1" },
      forceFormData: visit.forceFormData,
      preserveScroll: visit.preserveScroll,
      preserveState: visit.preserveState,
      errorBag: visit.errorBag,
      queryStringArrayFormat: visit.queryStringArrayFormat,
      only: visit.only,
      except: visit.except,
      replace: visit.replace,
      fresh: visit.fresh,
      reset: visit.reset,
      preserveUrl: visit.preserveUrl,
      showProgress: visit.showProgress,
      async: visit.async,
      // Forward the user's per-visit callbacks (useForm wraps these to
      // drive processing/errors; callers depend on onSuccess/onFinish
      // for closing dialogs, resetting state, etc.). Wrap onFinish so
      // we always clear our recovery flag, even if the user's callback
      // throws.
      ...visit.callbacks,
      onFinish: (replayVisit) => {
        recoveryInProgress = false;
        try {
          userOnFinish?.(replayVisit);
        } catch (error) {
          // Don't let a caller's onFinish throw stick our state — log
          // and swallow. Inertia's own error path will surface it.
          if (typeof console !== "undefined") console.error(error);
        }
      },
    });
  } catch (_error) {
    recoveryInProgress = false;
    // /csrf itself failed — last-ditch full reload preserves the URL.
    window.location.assign(window.location.href);
  }
}

async function refreshCsrfToken() {
  const response = await fetch(CSRF_REFRESH_PATH, {
    method: "GET",
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
  });

  if (!response.ok) throw new Error(`csrf refresh failed (${response.status})`);
}

function syncCsrfMetaFromCookie() {
  const token = readCookie("XSRF-TOKEN");
  if (!token) return;

  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta && meta.content !== token) meta.setAttribute("content", token);
}

function readCookie(name) {
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));
  if (!cookie) return "";
  return decodeURIComponent(cookie.slice(name.length + 1));
}

// Header bags arrive in a few shapes — plain object, axios's lowercased
// object, or a Headers instance. Read case-insensitively.
function readHeader(headers, name) {
  if (!headers) return undefined;
  if (typeof headers.get === "function") return headers.get(name) ?? undefined;
  const target = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === target) return headers[key];
  }
  return undefined;
}
